/**
 * Sélection de journaux de l'export comptable : le FEC et le classeur Excel ne contiennent que les journaux choisis
 * (tous si aucun n'est choisi). Fonctions pures : tout ce qui est lu est passé en paramètre.
 */

const round2 = value => Math.round((value + Number.EPSILON) * 100) / 100;

// Balance des comptes de tiers (comptes clients 411 + client, 419, 401) : un solde par compte et par client ou fournisseur, comme celle de l'export complet
function auxBalancesOf(entries) {
    const balances = new Map();
    for (const entry of entries) {
        if (!entry.compAuxNum && !entry.compteNum.startsWith('411')) continue;
        const key = `${entry.compteNum}|${entry.compAuxNum}`;
        const balance = balances.get(key) ?? {compteNum: entry.compteNum, compteLib: entry.compteLib, compAuxNum: entry.compAuxNum, compAuxLib: entry.compAuxLib || entry.compteLib, debit: 0, credit: 0, balance: 0};
        balance.debit = round2(balance.debit + entry.debit);
        balance.credit = round2(balance.credit + entry.credit);
        balance.balance = round2(balance.debit - balance.credit);
        balances.set(key, balance);
    }
    return [...balances.values()].sort((a, b) => a.compteNum.localeCompare(b.compteNum) || a.compAuxLib.localeCompare(b.compAuxLib));
}

/**
 * Export limité aux journaux sélectionnés : écritures, totaux, journaux et balance des tiers suivent la sélection.
 * Le lettrage et la numérotation restent ceux de l'export complet de la période : les écritures gardent leur numéro.
 * Sans sélection, ou avec tous les journaux, l'export est renvoyé tel quel. `selectedJournals` indique alors les journaux gardés.
 */
export function restrictToJournals(exportData, journalCodes) {
    if (!exportData || !journalCodes || journalCodes.length === 0) return exportData;
    const selected = new Set(journalCodes);
    if (exportData.journals.every(journal => selected.has(journal.code))) return exportData;

    const entries = exportData.entries.filter(entry => selected.has(entry.journalCode));
    return {
        ...exportData,
        entries,
        totals: {
            count: new Set(entries.map(entry => entry.ecritureNum)).size,
            lines: entries.length,
            debit: round2(entries.reduce((sum, entry) => sum + entry.debit, 0)),
            credit: round2(entries.reduce((sum, entry) => sum + entry.credit, 0)),
        },
        journals: exportData.journals.filter(journal => selected.has(journal.code)),
        balances: auxBalancesOf(entries),
        selectedJournals: exportData.journals.map(journal => journal.code).filter(code => selected.has(code)),
    };
}

// Suffixe des noms de fichiers d'un export limité à certains journaux (ex : « AC-BQ »), vide pour un export complet
export function journalsSuffix(exportData) {
    return exportData?.selectedJournals?.length ? exportData.selectedJournals.join('-') : '';
}
